/**
 * @file Auras « orbites » (gabarits `AuraStyle.rig`) : des corps lumineux (billboards) qui parcourent des figures analytiques autour du chat — cercles inclines,
 * helices, lissajous, huit, cometes, serpent, tornade, pluie, fontaine, couronne, lames... — avec des TRAINES RGB recalculees exactement (pas d'historique de
 * figure : on evalue le chemin a t - k*dt) et trainant dans le monde quand le chat se deplace (historique des positions du chat). En plus : figure au sol
 * (runes, mandala, pulsations), lueur centrale (soleil / noyau / singularite sombre). Cout : 1 lot de quads + 1 lot de rubans, aucune allocation par frame.
 */
import { Vector3 } from 'three';
import type { PerspectiveCamera, Scene } from 'three';
import type { AuraRig, AuraStyle, RigGround, RigOrbiter, RigShape } from '@config/types';
import { buildAtlas, Cell, hexRgb, hsv, RibbonBatch, SpriteBatch } from './FxBatch';
import type { CamBasis } from './FxBatch';
import type { Texture } from 'three';

const TAU = Math.PI * 2;
const HIST = 128;
const MAX_TRAIL_POINTS = 18;
const FADE_IN = 0.35;
const FADE_OUT = 0.25;

const SHAPE_CELL: Record<RigShape, Cell> = {
  orb: Cell.Disc,
  star: Cell.Star,
  diamond: Cell.Diamond,
  heart: Cell.Heart,
  note: Cell.Note,
  blade: Cell.Blade,
  flame: Cell.Flame,
  petal: Cell.Petal,
  square: Cell.Square,
  flake: Cell.Flake,
};

const GROUND_CELL: Record<RigGround, Cell> = {
  rune: Cell.RuneCircle,
  mandala: Cell.Mandala,
  star: Cell.StarRune,
  pulse: Cell.Pulse,
  rings: Cell.Rings,
  lotus: Cell.Lotus,
  checker: Cell.Checker,
  vortex: Cell.Vortex,
};

const hash01 = (n: number): number => {
  const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
};

/** Resultat de `place` (evite toute allocation). */
interface Placed {
  x: number;
  y: number;
  z: number;
  /** Multiplicateur de taille (queue du serpent...). */
  s: number;
}

const placed: Placed = { x: 0, y: 0, z: 0, s: 1 };

/** Applique inclinaison (autour de X) puis lacet (autour de Y) a un point du plan XZ. */
const tiltYaw = (px: number, pz: number, tilt: number, yaw: number, h: number): void => {
  const ct = Math.cos(tilt);
  const st = Math.sin(tilt);
  const y = -pz * st;
  const z = pz * ct;
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  placed.x = px * cy + z * sy;
  placed.z = -px * sy + z * cy;
  placed.y = h + y;
};

const frac = (v: number): number => v - Math.floor(v);

/** Position locale (par rapport au chat) du corps `i` de `n` du groupe `g` au temps `tt`. */
const place = (g: RigOrbiter, i: number, n: number, tt: number): void => {
  const ph = (i / n) * TAU;
  const a = g.speed * tt + ph;
  const r = g.radius;
  const tilt = g.tilt ?? 0;
  const yaw = g.yaw ?? 0;
  placed.s = 1;
  switch (g.path) {
    case 'circle':
    case 'crown':
    case 'blades': {
      tiltYaw(Math.cos(a) * r, Math.sin(a) * r, tilt, yaw, g.height);
      if (g.path === 'crown') placed.y += 0.1 * Math.sin(a * 3 + 1);
      if (g.path === 'blades') placed.y += 0.08 * Math.sin(a * 2);
      break;
    }
    case 'tilted':
      tiltYaw(Math.cos(a) * r, Math.sin(a) * r, tilt, yaw, g.height);
      break;
    case 'ellipse':
    case 'comet': {
      // vitesse variable (loi des aires) pour les cometes : plus vite pres du chat
      const aa = g.path === 'comet' ? a + 0.7 * Math.sin(a) : a;
      tiltYaw(Math.cos(aa) * r, Math.sin(aa) * r * (g.aspect ?? 0.6), tilt, yaw + (g.path === 'comet' ? ph * 0.15 : 0), g.height);
      break;
    }
    case 'helix': {
      const H = 2.4;
      const y = frac(i / n + (g.rise ?? 0.4) * tt * 0.4) * H;
      const ang = yaw + y * 2.3 + g.speed * tt * 0.25;
      placed.x = Math.cos(ang) * r;
      placed.z = Math.sin(ang) * r;
      placed.y = g.height + y;
      break;
    }
    case 'lissajous': {
      const u = g.speed * tt * 0.5 + ph + yaw;
      const ratio = g.aspect ?? 1.5;
      placed.x = Math.sin(3 * u) * r;
      placed.z = Math.sin(2 * u * ratio * 0.75 + 1.1) * r;
      placed.y = g.height + Math.sin(u * 1.5) * (g.rise ?? 0.4) * 0.8;
      break;
    }
    case 'eight': {
      const s = Math.sin(a);
      tiltYaw(s * r, s * Math.cos(a) * r * 1.3, tilt, yaw, g.height + 0.22 * Math.sin(2 * a));
      break;
    }
    case 'flutter': {
      const sd = hash01(i * 3.1 + 5);
      const sd2 = hash01(i * 7.7 + 9);
      const k = g.speed;
      placed.x = (Math.sin(k * tt * (0.7 + sd) + sd * 9) + 0.55 * Math.sin(k * tt * 1.9 + sd2 * 7)) * r * 0.62;
      placed.z = (Math.cos(k * tt * (0.6 + sd2) + sd2 * 5) + 0.55 * Math.sin(k * tt * 1.7 + sd * 3)) * r * 0.62;
      placed.y = g.height + Math.sin(k * tt * 1.3 + sd * 6) * 0.55 + 0.25 * Math.sin(k * tt * 3.1 + i);
      break;
    }
    case 'tornado': {
      const H = 2.6;
      const y = frac(i / n + (g.rise ?? 0.5) * tt * 0.4) * H;
      const rad = r * (0.18 + 0.82 * (y / H));
      const ang = g.speed * tt + ph + y * 1.6;
      placed.x = Math.cos(ang) * rad;
      placed.z = Math.sin(ang) * rad;
      placed.y = g.height + y;
      break;
    }
    case 'rain': {
      const H = 2.7;
      const sd = hash01(i * 4.3 + 2);
      const rad = r * (0.55 + 0.45 * hash01(i * 9.1 + 4));
      const ang = sd * TAU + g.speed * tt * 0.2;
      placed.x = Math.cos(ang) * rad;
      placed.z = Math.sin(ang) * rad;
      placed.y = g.height + H - frac(sd + (g.rise ?? 1.4) * tt * 0.4) * H;
      break;
    }
    case 'fountain': {
      const sd = hash01(i * 5.7 + 3);
      const u = frac(tt * g.speed * 0.25 + i / n);
      const ang = sd * TAU + Math.floor(tt * g.speed * 0.25 + i / n) * 1.9;
      const dist = r * u * (0.45 + 0.55 * hash01(i * 2.3 + 8));
      placed.x = Math.cos(ang) * dist;
      placed.z = Math.sin(ang) * dist;
      placed.y = g.height + 0.1 + (g.rise ?? 1) * 1.9 * u * (1 - u) * 2.2;
      break;
    }
    case 'snake': {
      // le corps i suit la tete avec un retard : meme figure, temps recule
      const t2 = tt - i * 0.075;
      const ang = g.speed * t2;
      const rad = r * (1 + 0.28 * Math.sin(0.7 * t2));
      placed.x = Math.cos(ang) * rad;
      placed.z = Math.sin(ang) * rad;
      placed.y = g.height + 0.8 * Math.sin(0.9 * t2) + (g.rise ?? 0.5) * 0.3 * Math.sin(2.1 * t2);
      placed.s = 1 - 0.55 * (i / n);
      break;
    }
  }
};

export class AuraRigRenderer {
  private readonly atlas: Texture;
  private readonly sprites: SpriteBatch;
  private readonly ribbons: RibbonBatch;
  private cur: AuraStyle | null = null;
  private fade = 0;
  private t = 0;
  private eco = false;
  private readonly cam: CamBasis = { right: new Vector3(), up: new Vector3(), pos: new Vector3() };
  // historique de position du chat (traines dans le monde)
  private readonly ht = new Float32Array(HIST);
  private readonly hx = new Float32Array(HIST);
  private readonly hz = new Float32Array(HIST);
  private hn = 0;
  private hHead = 0;
  // tampons de traine
  private readonly xs = new Float32Array(MAX_TRAIL_POINTS);
  private readonly ys = new Float32Array(MAX_TRAIL_POINTS);
  private readonly zs = new Float32Array(MAX_TRAIL_POINTS);
  private readonly cx = new Float32Array(MAX_TRAIL_POINTS);
  private readonly cz = new Float32Array(MAX_TRAIL_POINTS);
  private readonly rgb = [1, 1, 1];
  private readonly colA: [number, number, number] = [1, 1, 1];
  private readonly colB: [number, number, number] = [1, 1, 1];
  private readonly tmpDir = new Vector3();

  constructor(private readonly scene: Scene) {
    this.atlas = buildAtlas();
    this.sprites = new SpriteBatch(scene, this.atlas, 700, 5);
    this.ribbons = new RibbonBatch(scene, 900, 4);
  }

  setEco(eco: boolean): void {
    this.eco = eco;
  }

  /** Nombre d'objets dessines a la derniere frame (diagnostic / tests). */
  get drawn(): { sprites: number } {
    return { sprites: this.sprites.count };
  }

  private pushHistory(x: number, z: number): void {
    this.hHead = (this.hHead + 1) % HIST;
    this.ht[this.hHead] = this.t;
    this.hx[this.hHead] = x;
    this.hz[this.hHead] = z;
    this.hn = Math.min(HIST, this.hn + 1);
  }

  /** Position du chat il y a `ago` secondes (interpolee) ; au-dela de l'historique : la plus ancienne. */
  private catAt(ago: number, out: { x: number; z: number }): void {
    const target = this.t - ago;
    let i = this.hHead;
    for (let s = 0; s < this.hn; s++) {
      if ((this.ht[i] as number) <= target) {
        if (s === 0) {
          out.x = this.hx[i] as number;
          out.z = this.hz[i] as number;
          return;
        }
        const nx = (i + 1) % HIST;
        const t0 = this.ht[i] as number;
        const t1 = this.ht[nx] as number;
        const f = t1 > t0 ? (target - t0) / (t1 - t0) : 0;
        out.x = (this.hx[i] as number) + ((this.hx[nx] as number) - (this.hx[i] as number)) * f;
        out.z = (this.hz[i] as number) + ((this.hz[nx] as number) - (this.hz[i] as number)) * f;
        return;
      }
      i = (i - 1 + HIST) % HIST;
    }
    const o = (this.hHead - this.hn + 1 + HIST) % HIST;
    out.x = this.hx[o] as number;
    out.z = this.hz[o] as number;
  }

  private readonly at = { x: 0, z: 0 };

  /** Couleur du corps `i` du groupe : defile en teinte (rgb), fixe (a / b) ou blanc. */
  private colorOf(g: RigOrbiter, i: number, n: number, hueBase: number, out: number[]): void {
    switch (g.color ?? 'rgb') {
      case 'rgb':
        hsv(hueBase + (g.hueSpread ?? 0.33) * (n > 1 ? i / n : 0), 0.9, 1, out);
        break;
      case 'a':
        out[0] = this.colA[0];
        out[1] = this.colA[1];
        out[2] = this.colA[2];
        break;
      case 'b':
        out[0] = this.colB[0];
        out[1] = this.colB[1];
        out[2] = this.colB[2];
        break;
      case 'white':
        out[0] = out[1] = out[2] = 1;
        break;
    }
  }

  /**
   * Met a jour et dessine. `style` = aura equipee (seules celles qui portent `rig` sont gerees ici) ; (x, z) = position monde du chat ;
   * `camera` fournit la base de vue pour les billboards et les rubans.
   */
  update(style: AuraStyle | null, x: number, z: number, dt: number, camera: PerspectiveCamera): void {
    const wanted = style?.rig ? style : null;
    this.t += dt;
    this.pushHistory(x, z);
    if (this.cur !== wanted) {
      if (this.cur === null) this.cur = wanted;
      else {
        this.fade -= dt / FADE_OUT;
        if (this.fade <= 0) {
          this.cur = wanted;
          this.fade = 0;
        }
      }
    }
    if (this.cur !== null && this.cur === wanted) this.fade = Math.min(1, this.fade + dt / FADE_IN);
    else if (this.cur === null) this.fade = 0;
    this.sprites.begin();
    this.ribbons.begin();
    const style2 = this.cur;
    if (style2?.rig && this.fade > 0.002) this.draw(style2, style2.rig, x, z, camera);
    this.sprites.end();
    this.ribbons.end();
  }

  private draw(style: AuraStyle, rig: AuraRig, x: number, z: number, camera: PerspectiveCamera): void {
    camera.updateMatrixWorld();
    const cam = this.cam;
    cam.pos.copy(camera.position);
    cam.right.set(1, 0, 0).applyQuaternion(camera.quaternion);
    cam.up.set(0, 1, 0).applyQuaternion(camera.quaternion);
    const eco = this.eco;
    const fade = this.fade;
    const t = this.t;
    const hueBase = t * (rig.hueSpeed ?? 0.18);
    const [ar, ag, ab] = hexRgb(style.colors[0]);
    const [br, bg, bb] = hexRgb(style.colors[1]);
    this.colA[0] = ar;
    this.colA[1] = ag;
    this.colA[2] = ab;
    this.colB[0] = br;
    this.colB[1] = bg;
    this.colB[2] = bb;
    const sp = this.sprites;
    const rgb = this.rgb;

    // ---- figure au sol
    if (rig.ground) this.drawGround(rig.ground, x, z, hueBase, fade);

    // ---- lueur centrale
    if (rig.core) {
      const c = rig.core;
      const pulse = 1 + 0.12 * Math.sin(t * 3.1);
      // la lueur est decalee vers la camera : le corps du chat ne la cache pas (elle reste « devant la poitrine »)
      const cdx = cam.pos.x - x;
      const cdz = cam.pos.z - z;
      const cl = Math.hypot(cdx, cdz) || 1;
      const cx = x + (cdx / cl) * 0.6;
      const cz = z + (cdz / cl) * 0.6;
      if (c.kind === 'dark') {
        hsv(hueBase, 0.5, 1, rgb);
        sp.billboard(cam, Cell.Glow, cx, 0.95, cz, c.size * 3.4 * pulse, 0, rgb[0] as number, rgb[1] as number, rgb[2] as number, 0.45 * fade);
        sp.billboard(cam, Cell.Ring, cx, 0.95, cz, c.size * 1.9, t * 0.8, 1, 0.8, 0.5, 0.7 * fade);
        sp.billboard(cam, Cell.Disc, cx, 0.95, cz, c.size * 1.15, 0, 0.04, 0.0, 0.1, 0.97 * fade);
      } else {
        if (c.color === 'rgb') hsv(hueBase, 0.75, 1, rgb);
        else if (c.color === 'b') {
          rgb[0] = this.colB[0];
          rgb[1] = this.colB[1];
          rgb[2] = this.colB[2];
        } else {
          rgb[0] = this.colA[0];
          rgb[1] = this.colA[1];
          rgb[2] = this.colA[2];
        }
        sp.billboard(cam, Cell.Glow, cx, 0.95, cz, c.size * 3.0 * pulse, 0, rgb[0] as number, rgb[1] as number, rgb[2] as number, 0.5 * fade);
        sp.billboard(cam, Cell.Disc, cx, 0.95, cz, c.size * 0.9 * pulse, 0, 1, 1, 1, 0.55 * fade);
      }
    }

    // ---- corps + traines
    for (const g of rig.orbiters) {
      const total = g.count;
      const n = eco ? Math.max(1, Math.ceil(total / 2)) : total;
      const stride = eco && total > 1 ? 2 : 1;
      const K = g.trail > 0 ? Math.max(2, Math.min(MAX_TRAIL_POINTS, Math.round(4 + g.trail * (eco ? 6 : 13)))) : 1;
      const span = g.trail * 1.0;
      const dtk = K > 1 ? span / (K - 1) : 0;
      // position du chat dans le passé pour ce pas de traine (une fois par groupe)
      for (let k = 0; k < K; k++) {
        this.catAt(k * dtk, this.at);
        this.cx[k] = this.at.x;
        this.cz[k] = this.at.z;
      }
      const cell = SHAPE_CELL[g.shape];
      for (let j = 0; j < n; j++) {
        const i = j * stride;
        this.colorOf(g, i, total, hueBase, rgb);
        const cr = rgb[0] as number;
        const cg = rgb[1] as number;
        const cb = rgb[2] as number;
        let hx = 0;
        let hy = 0;
        let hz = 0;
        let size = g.size;
        for (let k = 0; k < K; k++) {
          place(g, i, total, t - k * dtk);
          const wx = placed.x + (this.cx[k] as number);
          const wz = placed.z + (this.cz[k] as number);
          this.xs[k] = wx;
          this.ys[k] = placed.y;
          this.zs[k] = wz;
          if (k === 0) {
            hx = wx;
            hy = placed.y;
            hz = wz;
            size = g.size * placed.s;
          }
        }
        if (K > 1 && this.ribbons.room > K + 1) {
          const w0 = size * 1.5 * (g.width ?? 0.9);
          this.ribbons.strip(cam, K, this.xs, this.ys, this.zs, w0, w0 * 0.05, 0.85 * fade, 0, cr, cg, cb, (g.color ?? 'rgb') === 'rgb', hueBase + (g.hueSpread ?? 0.33) * (total > 1 ? i / total : 0), -0.14 * g.trail);
        }
        // corps : halo + forme + coeur blanc
        sp.billboard(cam, Cell.Glow, hx, hy, hz, size * 2.7, 0, cr, cg, cb, 0.42 * fade);
        if (g.shape === 'blade' || g.shape === 'flame') {
          // oriente le long du deplacement (difference finie)
          place(g, i, total, t + 0.03);
          this.tmpDir.set(placed.x + (this.cx[0] as number) - hx, placed.y - hy, placed.z + (this.cz[0] as number) - hz);
          const len = this.tmpDir.length() || 1;
          this.tmpDir.multiplyScalar(1 / len);
          sp.oriented(cam, cell, hx, hy, hz, this.tmpDir.x, this.tmpDir.y, this.tmpDir.z, size * 1.5, size * 1.1, cr, cg, cb, 0.95 * fade);
        } else {
          sp.billboard(cam, cell, hx, hy, hz, size * 1.15, g.shape === 'orb' ? 0 : t * 2.5 + i, cr, cg, cb, 0.95 * fade);
        }
        sp.billboard(cam, Cell.Disc, hx, hy, hz, size * 0.5, 0, 1, 1, 1, 0.9 * fade);
      }
    }
  }

  private drawGround(kind: RigGround, x: number, z: number, hueBase: number, fade: number): void {
    const sp = this.sprites;
    const t = this.t;
    const rgb = this.rgb;
    hsv(hueBase, 0.85, 1, rgb);
    const r = rgb[0] as number;
    const g = rgb[1] as number;
    const b = rgb[2] as number;
    const Y = 0.06;
    switch (kind) {
      case 'pulse':
        for (let k = 0; k < 3; k++) {
          const u = frac(t * 0.55 + k / 3);
          hsv(hueBase + k * 0.17, 0.85, 1, rgb);
          sp.flat(Cell.Pulse, x, Y, z, 0.5 + u * 2.3, 0, rgb[0] as number, rgb[1] as number, rgb[2] as number, (1 - u) * 0.8 * fade);
        }
        break;
      case 'rings':
        sp.flat(Cell.Rings, x, Y, z, 1.9 + 0.1 * Math.sin(t * 3), t * 0.4, r, g, b, 0.75 * fade);
        hsv(hueBase + 0.4, 0.85, 1, rgb);
        sp.flat(Cell.Ring, x, Y + 0.01, z, 1.35, -t * 0.7, rgb[0] as number, rgb[1] as number, rgb[2] as number, 0.6 * fade);
        break;
      case 'vortex':
        sp.flat(Cell.Vortex, x, Y, z, 2.1, t * 1.6, r, g, b, 0.75 * fade);
        sp.flat(Cell.Glow, x, Y - 0.01, z, 1.6, 0, r, g, b, 0.3 * fade);
        break;
      case 'mandala':
        sp.flat(Cell.Mandala, x, Y, z, 2.3, t * 0.35, r, g, b, 0.8 * fade);
        hsv(hueBase + 0.5, 0.85, 1, rgb);
        sp.flat(Cell.Lotus, x, Y + 0.01, z, 1.2, -t * 0.6, rgb[0] as number, rgb[1] as number, rgb[2] as number, 0.8 * fade);
        break;
      default:
        sp.flat(GROUND_CELL[kind], x, Y, z, 2.0, kind === 'checker' ? 0 : t * 0.5, r, g, b, 0.8 * fade);
        sp.flat(Cell.Glow, x, Y - 0.01, z, 1.7, 0, r, g, b, 0.25 * fade);
        break;
    }
  }

  dispose(): void {
    this.sprites.dispose();
    this.ribbons.dispose();
    this.atlas.dispose();
  }
}
